import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  InputField,
  RadioGroupField,
  CheckboxField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './FONASUMFTransferDetails.css';
import alertIcon from '../../images/icon_exclaimation.svg';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import FundQuickSearch from '../FundQuickSearch/FundQuickSearch';
import { answeredNo, cleanTicker } from './utils';
import { isEmpty } from '../../core/util';
import { transformIntoQuickSearchFunds } from '../ManualInstitution/utils';
import { accountNameValidation } from '../../flows/shared/validations';

export const MessageIcon = () => {
  const text =
    'To complete the transfer, you must liquidate your assets after submitting this application. The term cash featured as the fund name serves as a placeholder only.';

  return (
    <div className="trp-message">
      <div className="icon">
        <img alt="iconImg" height="40px" src={alertIcon} />
      </div>
      <div className="alert-content">
        <span>{text}</span>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export const RenderCashBox = ({ preliquidated }) => {
  const {
    formProps: { accountName, accountNumber, transferBalance, transferAmount },
    appState: {
      store: {
        app: { externalAccounts }
      }
    }
  } = useContext(FlowContext);

  const defaults = {
    funds: (externalAccounts || []).map(account => ({
      [accountName.name]: account.accountName,
      [accountNumber.name]: account.accountNumber,
      [transferAmount.name]: account.estimatedTransferAmount,
      [transferBalance.name]: account.liquidationOption
    }))
  };

  const { sections } = useRepeatedSection('funds', defaults);

  return (
    <div className="sections">
      {answeredNo(preliquidated) && <MessageIcon />}

      {sections.map(({ key, getDefault, makeName }, idx) => {
        const transferBalanceDefault = getDefault(transferBalance.name);
        return (
          idx < 1 && (
            <div key={key} className="section">
              <div className="transfer-account-fields">
                <div className="account-name-input">
                  <InputField
                    title="Account Name"
                    value="Cash"
                    {...accountName}
                    name={makeName(accountName.name)}
                    defaultValue={getDefault(accountName.name)}
                  />
                </div>
                <div className="account-number-input">
                  <InputField
                    title="Account Number"
                    {...accountNumber}
                    name={makeName(accountNumber.name)}
                    defaultValue={getDefault(accountNumber.name)}
                    maxLength="30"
                  />
                </div>
              </div>

              <div className="transfer-amount-fields">
                <div className="transfer-type-input">
                  <RadioGroupField
                    title="Transfer Balance"
                    options={[
                      {
                        title: 'Full',
                        value: 'ALL',
                        defaultChecked: transferBalanceDefault === 'ALL'
                      },
                      {
                        title: 'Partial',
                        value: 'PARTIAL',
                        defaultChecked: transferBalanceDefault === 'PARTIAL'
                      }
                    ]}
                    horizontal
                    {...transferBalance}
                    name={makeName(transferBalance.name)}
                  />
                </div>
                <div className="transfer-amount-input">
                  <InputField
                    type="number"
                    title="Estimated Transfer Amount"
                    {...transferAmount}
                    name={makeName(transferAmount.name)}
                    defaultValue={getDefault(transferAmount.name)}
                  />
                </div>
              </div>
            </div>
          )
        );
      })}
      <style jsx>{styles}</style>
    </div>
  );
};

export const RenderBox = ({
  custodianFunds,
  setErrorFlag,
  cusips,
  setCusips
}) => {
  const {
    formProps: { accountName, accountNumber, transferBalance, transferAmount },
    appState: {
      store: {
        app: { externalAccounts }
      }
    }
  } = useContext(FlowContext);

  const [accountNames, setAccountNames] = useState(
    (externalAccounts || []).map(acct => acct.accountName)
  );

  const [manualEntry, setManualEntry] = useState(false);

  const fundQuickSearchFunds = transformIntoQuickSearchFunds(custodianFunds);

  const getSelectedCustodianFundDetails = fundName => {
    return fundQuickSearchFunds.find(f => f.name === fundName);
  };

  const updateCusips = (idx, value) => {
    let cusip = '';
    const updatedCusips = [...cusips];
    // Remove the ticker from value
    const selectedVal = cleanTicker(value).trim();
    // Retreive cusip for the fund
    const investmentObj = getSelectedCustodianFundDetails(selectedVal);
    if (investmentObj !== undefined) {
      cusip = investmentObj.cusip;
    }
    updatedCusips[idx] = cusip;
    setCusips(updatedCusips);
  };

  const updateAccountNames = (idx, value) => {
    const updatedAccountNames = [...accountNames];
    updatedAccountNames[idx] = value;
    setAccountNames(updatedAccountNames);
  };

  const accountNameAction = (idx, value) => {
    if (value === 'none') {
      setManualEntry(true);
      updateAccountNames(idx, value);
    } else {
      setManualEntry(false);
      updateAccountNames(idx, value);
      updateCusips(idx, value);
    }
  };

  /* function handler to update account names array
   when a section is removed */
  const handleRemoveSection = idx => {
    const filteredAccountNames = accountNames.filter(
      (_, index) => index !== idx
    );
    setAccountNames(filteredAccountNames);
  };

  const defaults = {
    funds: (externalAccounts || []).map(account => ({
      [accountName.name]: account.accountName,
      [accountNumber.name]: account.accountNumber,
      [transferAmount.name]: account.estimatedTransferAmount,
      [transferBalance.name]: account.liquidationOption
    }))
  };

  const { sections, addSection } = useRepeatedSection('funds', defaults);
  const sectionsRef = useRef();
  const [sameAccountChecked, toggleSameAccountChecked] = useState(false);

  const applyAccountNameToOtherSections = sameAccountCheckedCurrent => {
    // Check if checking or unchecking the checkbox
    if (sameAccountCheckedCurrent) {
      // Grab account name fields from all the sections
      const accountNumberNodes = [
        ...(sectionsRef?.current?.querySelectorAll(
          'input[name*=accountNumber]'
        ) || [])
      ];

      // Only do anything if the first section has account name
      // and there are multiple sections
      if (accountNumberNodes.length > 1 && accountNumberNodes[0].value) {
        const accountNumberToPopulate = accountNumberNodes[0].value;

        /* eslint-disable  no-param-reassign */
        accountNumberNodes.slice(1).forEach(node => {
          // Modify the value in other sections and dispatch a change event
          // to rerender the screen
          const descriptor = Object.getOwnPropertyDescriptor(node, 'value');
          if (descriptor && descriptor.configurable) {
            delete node.value;
          }
          node.value = accountNumberToPopulate;
          const event = new Event('change', { bubbles: true });
          node.dispatchEvent(event);
        });
        /* eslint-enable  no-param-reassign */
      }
    }
  };

  const addSectionAndCheckAccountNumber = () => {
    addSection();
    setTimeout(function() {
      applyAccountNameToOtherSections(sameAccountChecked);
    }, 50);
  };

  const toggleSameAccountCheckAndApply = () => {
    const toggledAccountCheck = !sameAccountChecked;
    toggleSameAccountChecked(toggledAccountCheck);
    applyAccountNameToOtherSections(toggledAccountCheck);
  };

  useEffect(() => {
    if (
      sections.length > 0 &&
      (accountNames.length !== sections.length ||
        accountNames.filter(name => isEmpty(name)).length > 0)
    ) {
      setErrorFlag(true);
    } else {
      setErrorFlag(false);
    }
  }, [sections, accountNames]);

  return (
    <div className="sections" ref={sectionsRef}>
      <div>
        <CheckboxField
          name="allAccountsSameNumber"
          className="checkbox-all-accounts-field"
          title={<> All of my accounts have the same account number {}</>}
          onClick={toggleSameAccountCheckAndApply}
        />
      </div>

      {sections.map(({ key, getDefault, makeName, removeSection }, idx) => {
        const transferBalanceDefault = getDefault(transferBalance.name);
        return (
          <div key={key} className="section">
            {idx > 0 && (
              <div className="remove-section-button">
                <MFBeaconButton
                  id="fonasuTransferRemoveFundSectionBtn"
                  data-name="fonasuTransferRemoveFundSectionBtn"
                  type="button"
                  noButtonMargin
                  onClick={() => {
                    removeSection();
                    handleRemoveSection(idx);
                  }}
                  variant="tertiary"
                  label="X"
                />
              </div>
            )}
            <div className="transfer-account-fields">
              <div className="account-name-input">
                {manualEntry || !isEmpty(accountNames[idx]) ? (
                  <InputField
                    title="Account Name"
                    {...accountName}
                    name={makeName(accountName.name)}
                    defaultValue={accountNames[idx]}
                    onChange={event =>
                      accountNameAction(idx, event.target.value)
                    }
                    validation={accountNameValidation}
                  />
                ) : (
                  <>
                    <div className="quick-search-label">
                      Search Account Name
                    </div>
                    <FundQuickSearch
                      allMutualFunds={fundQuickSearchFunds}
                      includeIcon={false}
                      mode="FONASU-transfer"
                      updateFilterText={text => accountNameAction(idx, text)}
                    />
                    {isEmpty(accountNames[idx]) && (
                      <div className="error-msg">Required</div>
                    )}
                  </>
                )}
              </div>

              <div className="account-number-input">
                <InputField
                  title="Account Number"
                  {...accountNumber}
                  name={makeName(accountNumber.name)}
                  defaultValue={getDefault(accountNumber.name)}
                  maxLength="30"
                />
              </div>
            </div>

            <div className="transfer-amount-fields">
              <div className="transfer-type-input">
                <RadioGroupField
                  title="Transfer Balance"
                  options={[
                    {
                      title: 'Full',
                      value: 'ALL',
                      defaultChecked: transferBalanceDefault === 'ALL'
                    },
                    {
                      title: 'Partial',
                      value: 'PARTIAL',
                      defaultChecked: transferBalanceDefault === 'PARTIAL'
                    }
                  ]}
                  horizontal
                  {...transferBalance}
                  name={makeName(transferBalance.name)}
                />
              </div>
              <div className="transfer-amount-input">
                <InputField
                  type="number"
                  title="Estimated Transfer Amount"
                  {...transferAmount}
                  name={makeName(transferAmount.name)}
                  defaultValue={getDefault(transferAmount.name)}
                />
              </div>
            </div>
          </div>
        );
      })}
      <MFBeaconButton
        id="fonasuTransferAddAnotherFundSectionBtn"
        data-name="fonasuTransferAddAnotherFundSectionBtn"
        type="button"
        onClick={addSectionAndCheckAccountNumber}
        variant="tertiary"
        label="+ Add Another Fund"
      />
      <style jsx>{styles}</style>
    </div>
  );
};
